import { Check } from 'lucide-react-native';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ProductKind } from '../types';

interface ProductKindSelectorProps {
  testIDPrefix: string;
  value: ProductKind;
  onChange: (kind: ProductKind) => void;
}

const KINDS: readonly ProductKind[] = ['product', 'service'];

const minimumTarget =
  Platform.OS === 'android'
    ? tokens.interaction.minimumTarget.android
    : tokens.interaction.minimumTarget.default;

export function ProductKindSelector({
  testIDPrefix,
  value,
  onChange,
}: Readonly<ProductKindSelectorProps>) {
  const { t } = useTranslation();

  return (
    <VStack spacing="xs">
      <Text variant="caption" color={colors.text.secondary} style={styles.label}>
        {t('onboardingWizard.products.kind.label')}
      </Text>
      <View
        accessibilityRole="radiogroup"
        accessibilityLabel={t('onboardingWizard.products.kind.label')}
        style={styles.group}
      >
        {KINDS.map((kind) => {
          const selected = kind === value;
          return (
            <Pressable
              key={kind}
              testID={`${testIDPrefix}-${kind}`}
              accessibilityRole="radio"
              accessibilityLabel={t(`onboardingWizard.products.kind.${kind}`)}
              accessibilityState={{ selected, checked: selected }}
              onPress={() => onChange(kind)}
              style={[styles.option, selected ? styles.optionSelected : styles.optionIdle]}
            >
              <HStack spacing="xs" align="center" justify="center">
                {selected ? <Check size={14} color={colors.primary[500]} /> : null}
                <Text
                  variant="caption"
                  color={selected ? colors.primary[500] : colors.text.secondary}
                  style={styles.label}
                >
                  {t(`onboardingWizard.products.kind.${kind}`)}
                </Text>
              </HStack>
            </Pressable>
          );
        })}
      </View>
    </VStack>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '600',
  },
  group: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  option: {
    flex: 1,
    minHeight: minimumTarget,
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
  },
  optionSelected: {
    borderColor: colors.primary[500],
    backgroundColor: colors.primary.subtle,
  },
  optionIdle: {
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
  },
});
